import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import api, { getActiveContainersTracking, scrapeContainerTracking, getContainerTrackingData, scrapePortTracking } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PortDashboard from '../components/PortDashboard';
import { TrackingTimeline } from '../components/TrackingTimeline';
import {
  Search, ExternalLink, Ship, Anchor, Package, RefreshCw, CheckCircle, XCircle,
  Clock, MapPin, Loader2, ChevronDown, ChevronUp, Navigation, Calendar, Building2,
  Radar,
} from 'lucide-react';

const CARRIER_LOGOS = {
  MSC: { bg: 'bg-[#002B5C]', text: 'text-white' },
  EVERGREEN: { bg: 'bg-[#00843D]', text: 'text-white' },
  COSCO: { bg: 'bg-[#003DA5]', text: 'text-white' },
  CMA_CGM: { bg: 'bg-[#002A5E]', text: 'text-white' },
  HAPAG: { bg: 'bg-[#FF6600]', text: 'text-white' },
  MAERSK: { bg: 'bg-[#42B0D5]', text: 'text-white' },
  ONE: { bg: 'bg-[#FF69B4]', text: 'text-white' },
  ZIM: { bg: 'bg-[#FFD700]', text: 'text-black' },
  YANG_MING: { bg: 'bg-[#006341]', text: 'text-white' },
  UNKNOWN: { bg: 'bg-secondary', text: 'text-muted-foreground' },
};

const PORT_TERMINAL_COLORS = {
  CONTECON: { bg: 'bg-[#EB4629]', text: 'text-white' },
  TIMSA: { bg: 'bg-[#009BDE]', text: 'text-white' },
  SSA: { bg: 'bg-[#1B3A5C]', text: 'text-white' },
};

export default function TrackingPage() {
  const [data, setData] = useState({ containers: [], total: 0, by_carrier: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [expandedContainer, setExpandedContainer] = useState(null);
  const [trackingDetail, setTrackingDetail] = useState({});
  const [scraping, setScraping] = useState({});
  const [scrapingAll, setScrapingAll] = useState(false);
  const [portScraping, setPortScraping] = useState({});
  const [portResults, setPortResults] = useState({});
  const [activeTab, setActiveTab] = useState('containers');
  const { t } = useI18n();

  useEffect(() => {
    loadTracking();
  }, []);

  const loadTracking = async () => {
    try {
      setLoading(true);
      const res = await getActiveContainersTracking();
      setData(res.data);
    } catch (err) {
      toast.error(t('common.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const filtered = data.containers.filter(c => {
    if (carrierFilter && (c.carrier_code || 'UNKNOWN') !== carrierFilter) return false;
    if (search) {
      const s = search.toUpperCase();
      return (
        c.container_number?.toUpperCase().includes(s) ||
        c.shipment_mbl?.toUpperCase().includes(s) ||
        c.client_name?.toUpperCase().includes(s)
      );
    }
    return true;
  });

  const handleScrapeOne = async (containerNumber) => {
    setScraping(prev => ({ ...prev, [containerNumber]: true }));
    try {
      const res = await scrapeContainerTracking(containerNumber);
      const result = res.data;
      // Always fetch detail data for the expanded row
      const detailRes = await getContainerTrackingData(containerNumber);
      setTrackingDetail(prev => ({ ...prev, [containerNumber]: { ...detailRes.data, scrapeResult: result } }));
      setExpandedContainer(prev => prev === containerNumber ? null : containerNumber);
      if (result.success) {
        toast.success(t('tracking.dataUpdated'));
      } else if (result.cached) {
        toast.info(t('tracking.usingCachedData'));
      } else {
        toast.warning(result.message || t('tracking.scrapeError'));
      }
    } catch (err) {
      toast.error(t('tracking.scrapeError'));
      setExpandedContainer(prev => prev === containerNumber ? null : containerNumber);
    } finally {
      setScraping(prev => ({ ...prev, [containerNumber]: false }));
    }
  };

  const handleScrapeAll = async () => {
    setScrapingAll(true);
    try {
      await api.post('/tracking/scrape-all');
      toast.success(t('tracking.scrapeAllStarted'));
    } catch (err) {
      toast.error(t('tracking.scrapeError'));
    } finally {
      setScrapingAll(false);
    }
  };

  const handlePortScrape = async (containerNumber, terminalCode) => {
    const key = `${containerNumber}-${terminalCode}`;
    setPortScraping(prev => ({ ...prev, [key]: true }));
    try {
      const res = await scrapePortTracking(containerNumber, terminalCode);
      const result = res.data;
      setPortResults(prev => ({ ...prev, [key]: result }));
      setExpandedContainer(containerNumber);
      if (result.success && result.total > 0) {
        toast.success(`${terminalCode}: ${result.total} ${t('tracking.portResultsFound')}`);
      } else if (result.success && result.total === 0) {
        toast.info(`${terminalCode}: ${t('tracking.portNoResults')}`);
      } else {
        toast.warning(result.message || t('tracking.portError'));
      }
    } catch (err) {
      toast.error(t('tracking.portError'));
    } finally {
      setPortScraping(prev => ({ ...prev, [key]: false }));
    }
  };

  const carrierBadge = (code, name) => {
    const style = CARRIER_LOGOS[code] || CARRIER_LOGOS.UNKNOWN;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        <Ship size={12} />
        {name || t('tracking.unknown')}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground tracking-tight" data-testid="tracking-page-title">
              {t('tracking.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('tracking.subtitle')}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-secondary/30 rounded-lg w-fit" data-testid="tracking-tabs">
          <button
            onClick={() => setActiveTab('containers')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'containers'
                ? 'bg-card text-foreground shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-containers"
          >
            <Radar size={15} />
            Contenedores
          </button>
          <button
            onClick={() => setActiveTab('port-dashboard')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'port-dashboard'
                ? 'bg-card text-foreground shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-port-dashboard"
          >
            <Anchor size={15} />
            Mapa Portuario
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'port-dashboard' ? (
          <PortDashboard />
        ) : (
          <>
        {/* Carrier Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <button
            onClick={() => setCarrierFilter('')}
            className={`p-3 rounded-lg border transition-all text-left ${
              !carrierFilter ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/50'
            }`}
            data-testid="filter-all"
          >
            <div className="flex items-center gap-2 mb-1">
              <Package size={16} className="text-primary" />
              <span className="text-xs text-muted-foreground">{t('tracking.allCarriers')}</span>
            </div>
            <p className="text-xl font-bold text-foreground">{data.total}</p>
          </button>
          {Object.entries(data.by_carrier).sort((a, b) => b[1].count - a[1].count).map(([code, info]) => (
            <button
              key={code}
              onClick={() => setCarrierFilter(carrierFilter === code ? '' : code)}
              className={`p-3 rounded-lg border transition-all text-left ${
                carrierFilter === code ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/50'
              }`}
              data-testid={`filter-carrier-${code}`}
            >
              <div className="flex items-center gap-2 mb-1">
                {carrierBadge(code, info.name)}
              </div>
              <p className="text-xl font-bold text-foreground">{info.count}</p>
            </button>
          ))}
        </div>

        {/* Search + Update All */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="relative max-w-md flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('tracking.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="tracking-search"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleScrapeAll}
            disabled={scrapingAll}
            data-testid="scrape-all-btn"
          >
            {scrapingAll ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            {t('tracking.updateAll')}
          </Button>
        </div>

        {/* Container List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground animate-pulse">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-lg">
            <Anchor size={40} className="mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-muted-foreground">{t('tracking.noActiveContainers')}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                  <th className="py-3 px-4 font-medium">{t('shipments.containerNumber')}</th>
                  <th className="py-3 px-4 font-medium">{t('shipments.carrier')}</th>
                  <th className="py-3 px-4 font-medium">{t('shipments.mbl')}</th>
                  <th className="py-3 px-4 font-medium">{t('shipments.client')}</th>
                  <th className="py-3 px-4 font-medium">{t('shipments.port')}</th>
                  <th className="py-3 px-4 font-medium">Terminal</th>
                  <th className="py-3 px-4 font-medium w-32">{t('tracking.track')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, idx) => {
                  const isExpanded = expandedContainer === c.container_number;
                  const detail = trackingDetail[c.container_number];
                  const isScraping = scraping[c.container_number];
                  const hasCarrier = !!c.carrier_code;
                  const terminals = c.port_terminals || [];

                  return (
                    <React.Fragment key={`${c.container_number}-${idx}`}>
                      <tr
                        className={`border-b border-border/50 hover:bg-secondary/10 transition-colors ${isExpanded ? 'bg-secondary/10' : ''}`}
                        data-testid={`tracking-row-${c.container_number}`}
                      >
                        <td className="py-3 px-4 font-mono text-sm font-medium">{c.container_number}</td>
                        <td className="py-3 px-4">
                          {carrierBadge(c.carrier_code || 'UNKNOWN', c.carrier_name)}
                        </td>
                        <td className="py-3 px-4">
                          <Link
                            to={`/shipments/${encodeURIComponent(c.shipment_mbl)}`}
                            className="text-sm text-primary hover:underline"
                          >
                            {c.shipment_mbl}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{c.client_name || '\u2014'}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{c.puerto || '\u2014'}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {terminals.map(pt => {
                              const ptKey = `${c.container_number}-${pt.code}`;
                              const isPtScraping = portScraping[ptKey];
                              const ptStyle = PORT_TERMINAL_COLORS[pt.code] || PORT_TERMINAL_COLORS.SSA;
                              return (
                                <button
                                  key={pt.code}
                                  onClick={() => pt.has_scraper ? handlePortScrape(c.container_number, pt.code) : window.open(pt.tracking_url, '_blank')}
                                  disabled={isPtScraping}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all hover:opacity-80 ${ptStyle.bg} ${ptStyle.text}`}
                                  title={pt.has_scraper ? `Consultar ${pt.short_name}` : `Abrir portal ${pt.short_name}`}
                                  data-testid={`port-btn-${c.container_number}-${pt.code}`}
                                >
                                  {isPtScraping ? <Loader2 size={10} className="animate-spin" /> : <Building2 size={10} />}
                                  {pt.short_name}
                                  {!pt.has_scraper && <ExternalLink size={8} />}
                                </button>
                              );
                            })}
                            {terminals.length === 0 && <span className="text-xs text-muted-foreground">{'\u2014'}</span>}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            {hasCarrier && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleScrapeOne(c.container_number)}
                                disabled={isScraping}
                                className="h-7 px-2 text-xs"
                                data-testid={`scrape-btn-${c.container_number}`}
                              >
                                {isScraping ? (
                                  <Loader2 size={12} className="mr-1 animate-spin" />
                                ) : (
                                  <RefreshCw size={12} className="mr-1" />
                                )}
                                {t('tracking.updateStatus')}
                              </Button>
                            )}
                            {c.tracking_url && (
                              <a
                                href={c.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-md text-xs font-medium transition-colors"
                                data-testid={`track-link-${c.container_number}`}
                              >
                                <ExternalLink size={12} />
                              </a>
                            )}
                            {isExpanded ? (
                              <button onClick={() => setExpandedContainer(null)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`collapse-btn-${c.container_number}`}>
                                <ChevronUp size={14} />
                              </button>
                            ) : (detail || Object.keys(portResults).some(k => k.startsWith(c.container_number))) && (
                              <button onClick={() => setExpandedContainer(c.container_number)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`expand-btn-${c.container_number}`}>
                                <ChevronDown size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Detail Row */}
                      {isExpanded && (
                        <tr className="bg-secondary/5" data-testid={`tracking-detail-${c.container_number}`}>
                          <td colSpan={7} className="px-4 py-4">
                            {/* Unified Timeline */}
                            <div className="mb-4 bg-card border border-border rounded-lg overflow-hidden">
                              <div className="px-3 py-2 border-b border-border/50 bg-muted/20">
                                <span className="text-xs font-medium text-muted-foreground">{t('tracking.timelineTitle')}</span>
                              </div>
                              <TrackingTimeline
                                container={c}
                                carrierData={detail?.tracking_data || detail?.scrapeResult}
                                portData={Object.entries(portResults || {})
                                  .filter(([k]) => k.startsWith(c.container_number))
                                  .reduce((acc, [, v]) => {
                                    if (v?.data) acc.data = [...(acc.data || []), ...v.data];
                                    return acc;
                                  }, { data: [] })}
                              />
                            </div>
                            <TrackingDetailPanel
                              detail={detail}
                              trackingUrl={c.tracking_url}
                              portResults={portResults}
                              containerNumber={c.container_number}
                              t={t}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function TrackingDetailPanel({ detail, trackingUrl, portResults, containerNumber, t }) {
  const td = detail?.tracking_data;
  const scrapeResult = detail?.scrapeResult;
  const hasData = detail?.has_data;
  const events = td?.events || scrapeResult?.events || [];

  // Collect port results for this container
  const containerPortResults = Object.entries(portResults || {})
    .filter(([k]) => k.startsWith(containerNumber + '-'))
    .map(([k, v]) => ({ terminalCode: k.split('-').pop(), ...v }));

  return (
    <div className="space-y-4">
      {/* Carrier Data Section */}
      {(hasData || scrapeResult?.success) && (
        <>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Carrier Tracking</p>
          {/* Status Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {td?.last_status && (
              <div className="flex items-start gap-2 p-3 bg-card border border-border rounded-lg">
                <CheckCircle size={14} className="text-green-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{t('tracking.scrapedStatus')}</p>
                  <p className="text-sm font-medium text-foreground mt-0.5">{td.last_status}</p>
                </div>
              </div>
            )}
            {td?.last_location && (
              <div className="flex items-start gap-2 p-3 bg-card border border-border rounded-lg">
                <MapPin size={14} className="text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{t('tracking.location')}</p>
                  <p className="text-sm font-medium text-foreground mt-0.5">{td.last_location}</p>
                </div>
              </div>
            )}
            {td?.vessel && (
              <div className="flex items-start gap-2 p-3 bg-card border border-border rounded-lg">
                <Navigation size={14} className="text-indigo-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{t('tracking.vessel')}</p>
                  <p className="text-sm font-medium text-foreground mt-0.5">{td.vessel}</p>
                </div>
              </div>
            )}
            {td?.eta && (
              <div className="flex items-start gap-2 p-3 bg-card border border-border rounded-lg">
                <Calendar size={14} className="text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{t('tracking.eta')}</p>
                  <p className="text-sm font-medium text-foreground mt-0.5">{td.eta}</p>
                </div>
              </div>
            )}
          </div>

          {/* Port info */}
          {(td?.pol || td?.pod) && (
            <div className="flex items-center gap-4 text-sm">
              {td?.pol && (
                <span className="text-muted-foreground">
                  <span className="font-medium text-foreground">{t('tracking.origin')}:</span> {td.pol}
                </span>
              )}
              {td?.pol && td?.pod && <span className="text-muted-foreground">&rarr;</span>}
              {td?.pod && (
                <span className="text-muted-foreground">
                  <span className="font-medium text-foreground">{t('tracking.destination')}:</span> {td.pod}
                </span>
              )}
            </div>
          )}

          {/* Events Timeline */}
          {events.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t('tracking.events')}</p>
              <div className="space-y-0">
                {events.map((ev, i) => (
                  <div key={i} className="flex items-start gap-3 relative" data-testid={`tracking-event-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${i === 0 ? 'bg-primary' : 'bg-border'}`} />
                      {i < events.length - 1 && <div className="w-px h-full min-h-[28px] bg-border" />}
                    </div>
                    <div className="pb-3 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {ev.date && <span className="text-xs text-muted-foreground font-mono">{ev.date}</span>}
                        {ev.location && <span className="text-xs font-medium text-foreground">{ev.location}</span>}
                      </div>
                      {ev.description && <p className="text-xs text-muted-foreground mt-0.5">{ev.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {td?.last_scraped && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Clock size={11} />
              {t('tracking.lastScraped')}: {new Date(td.last_scraped).toLocaleString('es-MX')}
            </p>
          )}
        </>
      )}

      {/* No carrier data message */}
      {!hasData && !scrapeResult?.success && containerPortResults.length === 0 && (
        <div className="flex items-center gap-3 py-2" data-testid="tracking-no-data">
          <XCircle size={16} className="text-amber-500 shrink-0" />
          <p className="text-sm text-muted-foreground">{t('tracking.noAutoData')}</p>
        </div>
      )}

      {/* Port Terminal Results Section */}
      {containerPortResults.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Building2 size={13} />
            Terminal Portuaria
          </p>
          {containerPortResults.map((pr, prIdx) => (
            <PortResultCard key={prIdx} result={pr} />
          ))}
        </div>
      )}

      {/* Portal link always available */}
      {trackingUrl && (
        <a
          href={trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-md text-xs font-medium transition-colors"
          data-testid="tracking-portal-link"
        >
          <ExternalLink size={13} />
          {t('tracking.visitPortal')}
        </a>
      )}
    </div>
  );
}


function PortResultCard({ result }) {
  const { terminalCode, success, data, etapas, total, message, source } = result;
  const ptStyle = PORT_TERMINAL_COLORS[terminalCode] || PORT_TERMINAL_COLORS.SSA;

  if (!success || !data || data.length === 0) {
    return (
      <div className="p-3 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ptStyle.bg} ${ptStyle.text}`}>
            <Building2 size={11} />
            {terminalCode}
          </span>
          <span className="text-xs text-muted-foreground">{message || 'Sin resultados'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-card border border-border rounded-lg space-y-3" data-testid={`port-result-${terminalCode}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ptStyle.bg} ${ptStyle.text}`}>
          <Building2 size={11} />
          {source || terminalCode}
        </span>
        <span className="text-xs text-muted-foreground">{total} resultado(s)</span>
      </div>

      {/* Container data from port */}
      {data.map((item, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-xs">
          {item.contenedor && (
            <div><span className="text-muted-foreground">Contenedor:</span> <span className="font-medium">{item.contenedor}</span>{item.tipo_contenedor && ` (${item.tipo_contenedor})`}</div>
          )}
          {item.bl && (
            <div><span className="text-muted-foreground">B/L:</span> <span className="font-medium">{item.bl}</span></div>
          )}
          {item.buque && (
            <div><span className="text-muted-foreground">Buque:</span> <span className="font-medium">{item.buque}</span></div>
          )}
          {item.linea_naviera && (
            <div><span className="text-muted-foreground">Naviera:</span> <span className="font-medium">{item.linea_naviera}</span></div>
          )}
          {item.fecha_entrada && (
            <div><span className="text-muted-foreground">Entrada:</span> <span className="font-medium">{item.fecha_entrada}</span></div>
          )}
          {item.fecha_salida && (
            <div><span className="text-muted-foreground">Salida:</span> <span className="font-medium">{item.fecha_salida}</span></div>
          )}
          {item.tipo_tramite && (
            <div><span className="text-muted-foreground">Tramite:</span> <span className="font-medium">{item.tipo_tramite}</span></div>
          )}
          {item.estatus && (
            <div><span className="text-muted-foreground">Estatus:</span> <span className="font-medium">{item.estatus}</span></div>
          )}
          {item.medio_transporte && (
            <div><span className="text-muted-foreground">Transporte:</span> <span className="font-medium">{item.medio_transporte}</span></div>
          )}
          {item.dias_en_patio && (
            <div><span className="text-muted-foreground">Dias patio:</span> <span className="font-medium">{item.dias_en_patio}</span></div>
          )}
        </div>
      ))}

      {/* Etapas (Contecon stages) */}
      {etapas && etapas.length > 0 && (
        <div>
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">Etapas del tramite</p>
          <div className="flex flex-wrap gap-2">
            {etapas.map((e, i) => (
              <div
                key={i}
                className={`px-2.5 py-1.5 rounded-md text-xs border ${
                  e.estatus === 'AC' && e.fecha
                    ? 'bg-green-500/10 border-green-500/30 text-green-700'
                    : 'bg-secondary/50 border-border text-muted-foreground'
                }`}
                data-testid={`port-etapa-${i}`}
              >
                <span className="font-medium">{i + 1}. {e.nombre}</span>
                {e.fecha && <span className="ml-1.5 font-mono text-[10px]">{e.fecha}</span>}
                {!e.fecha && <span className="ml-1.5 italic text-[10px]">Pendiente</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
